import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { BookingDTO } from '@lkp/parking-sdk';
import { FindBookings } from 'src/app/core/states/bookings/booking.actions';
import { BookingsState } from 'src/app/core/states/bookings/bookings.state';
import { DateService } from 'src/app/core/services/date.service';

@Component({
  selector: 'app-driver-search-dialog',
  templateUrl: './driver-search-dialog.component.html',
  styleUrls: ['./driver-search-dialog.component.scss'],
  standalone: false,
})
export class DriverSearchDialogComponent {
  searchInput = new FormControl<string | null>(null);
  showSpinner = false;
  termTooShort = false;
  termNotFound?: string;
  results: BookingDTO[] | undefined;

  private readonly onDestroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly dialogRef: MatDialogRef<DriverSearchDialogComponent>,
    private readonly dateService: DateService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    // Listen for search term changes
    this.searchInput.valueChanges
      .pipe(debounceTime(300), takeUntil(this.onDestroy$))
      .subscribe((term) => this.search(term));

    // Listen to search results from state
    this.store
      .select(BookingsState.searchResultUpcoming)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((upcoming) => this.applyResults(upcoming, 'upcoming'));

    this.store
      .select(BookingsState.searchResultHistory)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((history) => this.applyResults(history, 'history'));
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  search(term: string | null | undefined): void {
    this.results = undefined;
    if (typeof term !== 'string' || !term.trim().length) {
      this.termNotFound = undefined;
      this.termTooShort = false;
      this.showSpinner = false;
      return;
    }
    if (term.length < 3) {
      this.termNotFound = undefined;
      this.termTooShort = true;
      this.showSpinner = false;
      return;
    }

    this.termTooShort = false;
    this.showSpinner = true;
    this.store
      .dispatch(new FindBookings(term))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.showSpinner = false;
      });
  }

  private upcomingBase: BookingDTO[] | undefined;
  private historyBase: BookingDTO[] | undefined;

  private applyResults(items: BookingDTO[] | undefined, kind: 'upcoming' | 'history') {
    if (kind === 'upcoming') this.upcomingBase = items || undefined;
    else this.historyBase = items || undefined;

    const up = this.upcomingBase ? this.filterOutCallCustomers(this.upcomingBase) : [];
    const hi = this.historyBase ? this.filterOutCallCustomers(this.historyBase) : [];

    const merged = [...up, ...hi];
    this.results = merged.length ? merged : undefined;
    this.termNotFound = this.results ? undefined : this.searchInput.value || undefined;
  }

  private filterOutCallCustomers(bookings: BookingDTO[]): BookingDTO[] {
    return bookings.filter((b: any) => {
      const hasFlight = !!(
        b?.arrivalFlight?.flightNumber ||
        b?.departureFlight?.flightNumber ||
        b?.flightNumber ||
        b?.arrivalFlightNumber ||
        b?.departureFlightNumber
      );
      return hasFlight;
    });
  }

  formatDate(date: string | Date | undefined): string {
    return date ? this.dateService.formatDate(date) : '';
  }

  select(b: BookingDTO) {
    if (!b?.bookingId) return;
    this.dialogRef.close();
    this.router.navigate(['bokning', b.bookingId]);
  }

  close(): void {
    this.dialogRef.close();
  }
}
